import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff, ArrowRight, Download, Users, ListChecks } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const HERO_BG = "https://images.pexels.com/photos/13863730/pexels-photo-13863730.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

const features = [
  { icon: Download, title: "CSV & Excel Export", desc: "Export members, invoices and reports in one click." },
  { icon: Users, title: "Staff, Trainer & Member Roles", desc: "Role-based access across the whole platform." },
  { icon: ListChecks, title: "Attendance, Plans & Billing", desc: "Everything to run your gym in one place." },
];

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${BACKEND_URL}/api/auth/login`, { email, password });
      navigate("/admin");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0B0F1A] admin-scope">
      {/* Left hero */}
      <div className="hidden lg:flex w-1/2 relative flex-col justify-between p-14 xl:p-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="Gym" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F1A]/95 via-[#0B0F1A]/70 to-[#0B0F1A]/40" />
        </div>

        <div className="relative z-10 flex items-center gap-3">
          <img src="/logo.png" alt="SVT Golden" className="h-12 w-12 object-contain" onError={(e) => { e.target.style.display = "none"; }} />
          <span className="text-[#FACC15] font-bold text-2xl tracking-tight">SVT GOLDEN</span>
        </div>

        <div className="relative z-10 max-w-lg">
          <h1 className="text-5xl xl:text-6xl font-bold text-white tracking-tighter leading-[1.05]">
            Powering <span className="text-[#94A3B8]">Bengaluru's</span><br />
            <span className="text-[#FACC15]">Elite Gym & Studio.</span>
          </h1>
          <p className="text-[#94A3B8] mt-6 text-base leading-relaxed max-w-md">
            Members, billing, attendance, classes, personal training and analytics — managed from a single powerful dashboard.
          </p>
        </div>

        <div className="relative z-10 max-w-md">
          <p className="text-xs uppercase tracking-[0.2em] font-mono text-white mb-5">Feature Updates & Announcements</p>
          <div className="space-y-4">
            {features.map((f, i) => (
              <div key={i} className="flex gap-3 items-start">
                <div className="text-[#FACC15] mt-0.5 shrink-0"><f.icon size={18} /></div>
                <div>
                  <p className="text-sm font-semibold text-white uppercase tracking-wide">{f.title}</p>
                  <p className="text-xs text-[#94A3B8] mt-0.5">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center p-8 sm:p-12 bg-[#0B0F1A] relative">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-10 justify-center">
            <img src="/logo.png" alt="SVT Golden" className="h-10 w-10 object-contain" onError={(e) => { e.target.style.display = "none"; }} />
            <span className="text-[#FACC15] font-bold text-xl tracking-tight">SVT GOLDEN</span>
          </div>

          <h2 className="text-3xl font-bold text-white tracking-tight">Admin Dashboard</h2>
          <p className="text-[#94A3B8] mt-2 mb-8">Enter your credentials to login</p>

          {error && <p data-testid="login-error" className="text-accent_danger text-sm mb-6 bg-accent_danger/10 px-4 py-3 rounded-lg border border-accent_danger/30">{error}</p>}

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#94A3B8] mb-2 block">Mobile Number or Email Address</label>
              <input
                data-testid="login-email-input"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e.g. admin@svtgolden.com"
                required
                className="w-full bg-[#131A26] border border-[#243047] rounded-lg px-4 py-3.5 text-white placeholder:text-[#94A3B8]/50 focus:outline-none focus:ring-1 focus:ring-[#FACC15] focus:border-[#FACC15] transition-colors"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#94A3B8]">Password</label>
                <span className="text-[11px] font-mono uppercase tracking-[0.1em] text-[#FACC15] cursor-pointer hover:text-[#EAB308] transition-colors">Lost your password?</span>
              </div>
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full bg-[#131A26] border border-[#243047] rounded-lg px-4 py-3.5 pr-12 text-white placeholder:text-[#94A3B8]/50 focus:outline-none focus:ring-1 focus:ring-[#FACC15] focus:border-[#FACC15] transition-colors"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#FACC15] transition-colors">
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-admin-submit-btn"
              className="w-full py-4 bg-[#FACC15] text-[#0B0F1A] font-bold uppercase tracking-[0.1em] text-sm rounded-lg hover:bg-[#EAB308] transition-all hover:-translate-y-0.5 disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-black/30"
            >
              {loading ? "Authenticating..." : "Login to Dashboard"}
              {!loading && <ArrowRight size={18} />}
            </button>
          </form>

          <div className="mt-16 text-center">
            <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#94A3B8]/70">© {new Date().getFullYear()} SVT Golden Fitness. All Rights Reserved.</p>
            <div className="flex justify-center gap-6 mt-2">
              <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#94A3B8]/70 hover:text-[#FACC15] cursor-pointer transition-colors">Privacy Policy</span>
              <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#94A3B8]/70 hover:text-[#FACC15] cursor-pointer transition-colors">Terms of Service</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
